import * as $ from 'jquery';
import { VertexBuffer } from '@babylonjs/core';
import { CLASS_CUSTOM_SIZE_HIDDEN } from '../ssb_constants';
import { LEGACY_EXTRA_ROOF_DEPTH } from '../hardcode/constants';
/**
 * Gets the base width of the shed.
 *
 * @returns {number} The base width of the shed.
 */
export const getBaseWidth = () => {
	const customInput = $('#ssb-custom-width');
	if (
		customInput.length &&
		!$('#ssb-custom-size-fields-wrap').hasClass(CLASS_CUSTOM_SIZE_HIDDEN)
	) {
		return Number(customInput.val()) || 0;
	}

	return Number($('.ssb-option.size-area').children('option:selected').data('size-width'));
};

/**
 * Gets the base depth of the shed.
 *
 * @returns {number} The base depth of the shed.
 */
export const getBaseDepth = () => {
	const customInput = $('#ssb-custom-length');
	if (
		customInput.length &&
		!$('#ssb-custom-size-fields-wrap').hasClass(CLASS_CUSTOM_SIZE_HIDDEN)
	) {
		return Number(customInput.val()) || 0;
	}

	return Number($('.ssb-option.size-area').children('option:selected').data('size-depth'));
};

/**
 * Gets the base height of the shed.
 *
 * @returns {number} The base height of the shed.
 */
export const getBaseHeight = () => {
	const customInput = $('#ssb-custom-height');
	if (
		customInput.length &&
		!$('#ssb-custom-size-fields-wrap').hasClass(CLASS_CUSTOM_SIZE_HIDDEN)
	) {
		return Number(customInput.val()) || 0;
	}

	return Number(
		$('#ssb-configurator-size-height').children('option:selected').data('size-height'),
	);
};

/**
 * Rounds a number to 3 decimal places.
 *
 * @param {number} number - The number to round.
 * @returns {number} The rounded number.
 */
export const toFixed3 = (number: Number) => {
	return Number(Number(number).toFixed(3));
};

/**
 * Calculates the sine of a given angle in degrees.
 *
 * @param {number} degrees - Angle in degrees.
 * @returns {number} The sine of the given angle.
 */

export const getSinFromDegrees = (degrees) => {
	return Math.sin((degrees * Math.PI) / 180);
};

/**
 * Calculates the cosine of a given angle in degrees.
 *
 * @param {number} degrees Angle in degrees.
 * @returns {number} The cosine of the given angle.
 */
export const getCosFromDegrees = (degrees) => {
	return Math.cos((degrees * Math.PI) / 180);
};

/**
 * Calculates the tangent of a given angle in degrees.
 *
 * @param {number} degrees Angle in degrees.
 * @returns {number} The tangent of the given angle.
 */
export const getTanFromDegrees = (degrees) => {
	return Math.tan((degrees * Math.PI) / 180);
};

/**
 * Converts a roof pitch ratio to a roof pitch in degrees.
 *
 * @param {number} ratio - The roof pitch ratio.
 * @returns {number} The roof pitch in degrees.
 */
export const convertToDegreesFromRatio = (ratio): number => {
	return toFixed3((Math.atan(ratio / 12) * 180) / Math.PI);
};

/**
 * Calculates the roof pitch of a given angle in radians.
 *
 * @param {number} opposite - The opposite side of the triangle.
 * @param {number} [adjacent=12] - The adjacent side of the triangle.
 * @returns {number} The roof pitch of the given angle in radians.
 */
export const getRoofPitchFromRadian = (opposite, adjacent = 12): number => {
	return toFixed3((Math.atan(opposite / adjacent) * 180) / Math.PI);
};

/**
 * Converts a measurement in feet to a string representation of the measurement in feet and inches.
 * If the measurement is less than 0, returns '0'.
 * @param {number} feet - The measurement in feet.
 * @returns {string} The measurement in feet and inches.
 */
export const convertUnit = (feet) => {
	if (isNaN(feet) || isNaN(parseFloat(feet))) {
		return feet;
	}
	if (0 >= feet) {
		return '0';
	}

	let integer = Math.floor(feet);
	const decimal = Number(feet) - Number(integer);
	let feetUnit = '';
	let inchUnit = '';
	let tmp: any = 0;
	if (decimal > 0) {
		if (isShowMeasurementsInDecimal()) {
			tmp = parseFloat((decimal * 12).toFixed(2));
		} else {
			tmp = parseInt((decimal * 12).toFixed(0));
		}
		if (tmp > 0) {
			if (tmp === 12) {
				integer += 1;
			} else {
				if (
					isShowMeasurementsInDecimal() &&
					'fraction' === getMeasurementsDecimalFormat()
				) {
					let tmpInchUnit: any = Number(tmp);
					let tmpInt = Math.floor(tmp);
					let tmpDecimal = tmpInchUnit % 1;
					let faction: any = convertDecimalToFaction(tmpDecimal.toFixed(2));
					inchUnit = tmpInt + '" ' + (faction ? faction.display : '');
				} else {
					inchUnit = tmp + '"';
				}
			}
		}
	}

	if (integer > 0) {
		feetUnit = integer + "' ";
	}

	return feetUnit + inchUnit;
};

/**
 * Converts a decimal number into a fraction.
 *
 * @param {number} _decimal - The decimal number to convert.
 * @returns {false | {top: number, bottom: number, display: string}}
 *          Returns false if the decimal is 0, otherwise an object containing:
 *          - 'top': The numerator of the fraction.
 *          - 'bottom': The denominator of the fraction.
 *          - 'display': A string representation of the fraction in the format "numerator/denominator".
 */

export const convertDecimalToFaction = (_decimal) => {
	if (0 == _decimal) return false;
	var top = _decimal.toString().replace(/\d+[.]/, '');
	var bottom = Math.pow(10, top.length);
	if (_decimal > 1) {
		top = +top + Math.floor(_decimal) * bottom;
	}
	var x = getDenominator(top, bottom);
	return {
		top: top / x,
		bottom: bottom / x,
		display: top / x + '/' + bottom / x,
	};
};

/**
 * Calculates the greatest common divisor of two numbers using the Euclidean algorithm.
 *
 * @param {number} a - The first number.
 * @param {number} b - The second number.
 * @returns {number} The greatest common divisor of the two numbers.
 */
export const getDenominator = (a, b) => {
	return b ? getDenominator(b, a % b) : a;
};

/**
 * Checks if the current settings are to show measurements in decimal format.
 *
 * @returns {boolean} True if show measurements in decimal, false otherwise.
 */
export const isShowMeasurementsInDecimal = () => {
	return '1' == (<any>window).shedpro.show_measurements_in_decimal;
};

/**
 * Retrieves the current format for displaying measurements.
 *
 * @returns {string} The current format, either 'fraction' or 'decimal'.
 */
export const getMeasurementsDecimalFormat = () => {
	return 'fraction' == (<any>window).shedpro.measurements_format ? 'fraction' : 'decimal';
};

export const getRunwayOrientation = (): string => {
	const runwayElement = '.ssb-configurator-upgrade-new.runway-side-orientation input:checked';
	const runwayOrientation = $(runwayElement).val() as string;
	if (runwayOrientation) {
		return runwayOrientation.trim();
	}

	return 'end';
};

/**
 * Calculate angle BAC (in degrees) from the lengths of the three sides of the triangle
 * @param {number} AB - length of side AB
 * @param {number} AC - length of side AC
 * @param {number} BC - length of side BC
 * @returns {number} angle BAC (degrees)
 */
export const calcAngleBAC = (AB: number, AC: number, BC: number): number => {
	// Applying the cosine theorem
	const numerator = AB ** 2 + AC ** 2 - BC ** 2;
	const denominator = 2 * AB * AC;
	const cosA = numerator / denominator;

	// Convert from radians to degrees
	const angleA = Math.acos(cosA) * (180 / Math.PI);

	return angleA;
};

/**
 * Retrieves the highest z-coordinate of a shape (i.e. the top of the wall).
 *
 * @param {Object[]} shape - An array of objects with x, y, and z coordinates.
 * @returns {number} The highest z-coordinate of the shape.
 */
export const getTopOfWall = (shape): number => {
	let topWall: number = shape[0].z;

	for (let i = 1; i < shape.length; i++) {
		const z = shape[i].z;
		if (z > topWall) topWall = z;
	}

	return topWall;
};

/**
 * Retrieves the lowest z-coordinate of a shape (i.e. the bottom of the wall).
 *
 * @param {Object[]} shape - An array of objects with x, y, and z coordinates.
 * @returns {number} The lowest z-coordinate of the shape.
 */
export const getBottomOfWall = (shape): number => {
	let bottomWall: number = shape[0].z;

	for (let i = 1; i < shape.length; i++) {
		const z = shape[i].z;
		if (z < bottomWall) bottomWall = z;
	}

	return bottomWall;
};

/**
 * Calculate projected area (sqft) of a mesh on a given plane
 * using Convex Hull (Monotonic Chain algorithm)
 *
 * @param {BABYLON.Mesh} mesh
 * @param {"XZ" | "XY" | "YZ"} plane - projection plane
 * @returns {number} area
 */
export const getSqftMesh = (mesh, plane) => {
	// ======================================================
	// 1. Get vertex positions
	// ======================================================
	const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
	if (!positions) return 0;

	// ======================================================
	// 2. Project 3D vertices to 2D based on plane
	// ======================================================
	const points2D = [];

	for (let i = 0; i < positions.length; i += 3) {
		const x = positions[i];
		const y = positions[i + 1];
		const z = positions[i + 2];

		switch (plane) {
			case 'XZ':
				points2D.push([x, z]);
				break;
			case 'XY':
				points2D.push([x, y]);
				break;
			case 'YZ':
				points2D.push([y, z]);
				break;
		}
	}

	// ======================================================
	// 3. Remove duplicate points (floating precision safe)
	// ======================================================
	const uniquePoints = [];
	const seen = new Set();

	for (const [a, b] of points2D) {
		const key = `${a.toFixed(4)},${b.toFixed(4)}`;
		if (!seen.has(key)) {
			seen.add(key);
			uniquePoints.push([a, b]);
		}
	}

	// ======================================================
	// 4. Sort points (required for convex hull)
	// ======================================================
	uniquePoints.sort((a, b) => a[0] - b[0] || a[1] - b[1]);

	// Cross product helper (2D)
	const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

	// ======================================================
	// 5. Build lower hull
	// ======================================================
	const lower = [];
	for (const p of uniquePoints) {
		while (
			lower.length >= 2 &&
			cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
		) {
			lower.pop();
		}
		lower.push(p);
	}

	// ======================================================
	// 6. Build upper hull
	// ======================================================
	const upper = [];
	for (const p of uniquePoints.slice().reverse()) {
		while (
			upper.length >= 2 &&
			cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
		) {
			upper.pop();
		}
		upper.push(p);
	}

	// ======================================================
	// 7. Combine lower & upper hull to get convex hull
	// ======================================================
	const hull = lower.slice(0, -1).concat(upper.slice(0, -1));

	// ======================================================
	// 8. Calculate polygon area (Shoelace formula)
	// ======================================================
	let area = 0;

	for (let i = 0; i < hull.length; i++) {
		const [x1, y1] = hull[i];
		const [x2, y2] = hull[(i + 1) % hull.length];
		area += x1 * y2 - x2 * y1;
	}

	const areaFinal = Math.abs(area) * 0.5;

	// ======================================================
	// 9. Return rounded area
	// ======================================================
	return Number(areaFinal.toFixed(2));
};

/**
 * Gets the closest runner item based on target width.
 *
 * @param {any[]} runners - List of runner items.
 * @param {number} targetWidth - Target width value to compare.
 * @returns {any | null} The closest runner item or null if runners is empty.
 */
export const getClosestRunner = (runners: any[], targetWidth: number): any | null => {
	if (!runners || runners.length === 0) {
		return null;
	}

	return runners.reduce((closest, item) => {
		const getDistance = (target: number, min: number, max: number) => {
			if (target < min) return min - target;
			if (target > max) return target - max;
			return 0;
		};

		const closestDist = getDistance(
			targetWidth,
			Number(closest['min_width']),
			Number(closest['max_width']),
		);
		const itemDist = getDistance(
			targetWidth,
			Number(item['min_width']),
			Number(item['max_width']),
		);

		return itemDist < closestDist ? item : closest;
	});
};

/**
 * Calculates the extra depth of the roof, from a legacy hardcoded override
 * or the bounding box of the roof metal clone mesh, whichever applies.
 *
 * @returns {number} The calculated extra roof depth, or 0 if bbMinZ is not available.
 */
export const calculateExtraRoofDepth = ({
	shedType,
	shedTypeVersion,
	initialDepth,
	roofTopBoneZ,
	bbMinZ,
}: {
	shedType: string;
	shedTypeVersion: string;
	initialDepth: number;
	roofTopBoneZ: number | undefined;
	bbMinZ: number | undefined;
}): number => {
	// Check for legacy extra roof depth first
	if (isLegacyExtraRoofDepth(shedType, shedTypeVersion)) {
		return getLegacyExtraRoofDepth(shedType, shedTypeVersion);
	}

	if (bbMinZ === undefined) return 0;

	return roofTopBoneZ !== undefined
		? (Math.abs(bbMinZ) - Math.abs(roofTopBoneZ)) * 2
		: Math.abs(bbMinZ) * 2 - initialDepth;
};

/**
 * Checks whether a shedType/shedTypeVersion has a hardcoded legacy extra roof depth override.
 *
 * @param {string} shedType - The shed type.
 * @param {string} shedTypeVersion - The shed type version.
 * @returns {boolean} True if a legacy override exists for this combo.
 */
export const isLegacyExtraRoofDepth = (shedType: string, shedTypeVersion: string): boolean => {
	return LEGACY_EXTRA_ROOF_DEPTH.has(`${shedType}|${shedTypeVersion}`);
};

/**
 * Gets the hardcoded legacy extra roof depth override for a shedType/shedTypeVersion.
 *
 * @param {string} shedType - The shed type.
 * @param {string} shedTypeVersion - The shed type version.
 * @returns {number | undefined} The legacy extra roof depth, or undefined if no override exists.
 */
export const getLegacyExtraRoofDepth = (
	shedType: string,
	shedTypeVersion: string,
): number | undefined => {
	return LEGACY_EXTRA_ROOF_DEPTH.get(`${shedType}|${shedTypeVersion}`);
};
